/**
 * WS-HF — run ONE case-job stage on the operator's own Higgsfield subscription.
 *
 * The same shape as `generate/runDeviceGeneration.ts`, against the case-job device endpoints,
 * and for the same reason: the cloud still owns the job, the three photographs, the resolved
 * prompt and the result. Only who executes the call — and who is billed — changes.
 *
 *  1. `caseJobs.get(id)` → `promptText`, `inputs` (the signed reference URLs, in order) and
 *     `deviceAttemptToken` (sent back on every report).
 *  2. The SUBMISSION LATCH (`deviceSubmissionLatch.ts`), keyed per stage ATTEMPT —
 *     `caseJob:<id>:<stage>:<attempt>`. A remount or a reload mid-wait resumes the vendor job
 *     the latch recorded; an attempt whose outcome is unknown is refused, and never resent.
 *  3. `hfMcpEstimate` — what it will cost, before anything is spent.
 *  4. `hfMcpGenerate` with those inputs, in the order the cloud sent them.
 *  5. `hfMcpWait`, looping until terminal.
 *  6. `caseJobs.stageResult` on a completion — `caseJobs.stageFail` when the vendor ANSWERED
 *     with `failed` / `nsfw` / `canceled` (not charged) — or `caseJobs.stageHeld` with the
 *     vendor job id for everything lost AFTER the vendor accepted the body: a wait that timed
 *     out, a download that failed, an upload the cloud refused. `stage-fail` there would claim
 *     nothing was sent; the image may exist and may be billed, and the id is what the owner
 *     checks before paying again. `resumeHeldCaseJobStage` is the other half: "Check again"
 *     asks the vendor once for that id and posts the late image with `request_id`.
 *
 * **A non-completed job is NEVER reported as a success.** `failureSentence` is shared with the
 * single-stage runner so there is one definition of what counts as finished, and the ONLY path
 * that posts a result is a `completed` job with bytes actually downloaded.
 *
 * **The executor is the JOB's.** A job the cloud says is `cloud` is the worker's, whatever this
 * desktop is connected to today, and this function refuses to run it before anything is sent.
 */
import type { CaseJobView, JsonClient } from "@genz/platform";
import { HttpError, higgsfieldMcp, type HiggsfieldMcpClient } from "@genz/platform";
import { fetchResultBytes } from "../generate/candidates.js";
import { DEVICE_WAIT_BUDGET_MS, failureSentence } from "../generate/runDeviceGeneration.js";
import {
  UNKNOWN_SUBMISSION_MESSAGE,
  clearLatch,
  markSubmitted,
  markSubmitting,
  newIdempotencyKey,
  readLatch,
} from "../generate/deviceSubmissionLatch.js";
import { forgetRun } from "../generate/deviceRunRegistry.js";
import { listCreditsFor, noQuoteRefusal } from "../generate/higgsfieldModels.js";
import type { CaseStageKey } from "./caseJobState.js";

/** What the caller shows while a stage runs. */
export type CaseStagePhase = "reading" | "estimating" | "submitted" | "waiting" | "uploading" | "done";

export interface RunCaseJobStageInput {
  client: JsonClient;
  jobId: string;
  /** Which stage is running — the one the job must have open. */
  stage: CaseStageKey;
  /**
   * `attempts[stage]` as the job reports it BEFORE this run: 0 for the first run, 1 for the
   * approved retry. It keys the submission latch, so the retry is never mistaken for a resume
   * of the first run and the first run is never resubmitted under the retry's name.
   */
  attempt: number;
  /** The model this stage runs. Resolved from settings by the caller, not guessed here. */
  model: string;
  /** Model params (`resolution`, `quality`). */
  params?: Record<string, unknown>;
  /** Injectable for tests; defaults to the process-wide desktop bridge. */
  mcp?: HiggsfieldMcpClient;
  onPhase?: (phase: CaseStagePhase) => void;
  /** Fired with the estimate, before a single credit is spent. */
  onEstimate?: (credits: number) => void;
  logger?: { log: (level: string, message: string) => void };
  /** Injectable for tests; defaults to `candidates.ts`'s result fetch. */
  fetchBytes?: typeof fetchResultBytes;
  /** Tests only: how long the wait may take before the stage is held. */
  waitBudgetMs?: number;
  /**
   * The photos→job mapping this run was recalled through (`deviceRunRegistry`, keyed by
   * `caseRunKey`). Forgotten when a refused report shows the stage is no longer open at this
   * attempt: the next Start with the same photos must not be pinned to a job that moved on.
   */
  runKey?: string;
}

/** The latch key for one stage attempt of one job. */
export function caseStageLatchKey(jobId: string, stage: CaseStageKey, attempt: number): string {
  return `caseJob:${jobId}:${stage}:${attempt}`;
}

/** A failure that already reached the job (or needs no report), so the caller must not post
 *  it again. Carries the sentence the operator sees. */
export class CaseStageError extends Error {
  constructor(message: string, readonly reported = false) {
    super(message);
    this.name = "CaseStageError";
  }
}

/** The vendor ANSWERED — the generation failed, was moderated or was canceled. Not charged, so
 *  the stage is failed rather than held: there is nothing to check. */
class VendorAnsweredError extends CaseStageError {}

/** The sentence for an attempt this device cannot safely repeat. `isStuckOnUnknownCaseSubmission`
 *  recognises it so the card can offer the way out. */
const UNKNOWN_ATTEMPT_MESSAGE =
  `Higgsfield was already asked to generate this stage and the ${UNKNOWN_SUBMISSION_MESSAGE}.`;

export function isStuckOnUnknownCaseSubmission(e: unknown): boolean {
  return e instanceof CaseStageError && e.message.includes(UNKNOWN_SUBMISSION_MESSAGE);
}

/**
 * Was this report refused because the attempt was superseded (409 `stale_attempt`)?
 *
 * The cloud issues a token per open device attempt and refuses a report that carries an older
 * one. The right response is to re-read the job — another run of it already moved the stage —
 * and never to submit again.
 */
export function isStaleAttempt(e: unknown): boolean {
  return e instanceof HttpError && e.status === 409
    && (e.body as { detail?: unknown } | undefined)?.detail === "stale_attempt";
}

const STALE_ATTEMPT_MESSAGE =
  "This attempt was superseded by another run of the job. The card shows the job as it stands now; nothing was submitted again.";

/** Is `job` open for THIS stage on the device path? */
function stageOpen(job: CaseJobView, stage: CaseStageKey): boolean {
  return job.stage === `${stage}_pending` || job.stage === `${stage}_running`;
}

export async function runCaseJobStage(input: RunCaseJobStageInput): Promise<CaseJobView> {
  const { client, jobId, stage, attempt, model, onPhase, onEstimate, logger } = input;
  const mcp = input.mcp ?? higgsfieldMcp();
  const fetchBytes = input.fetchBytes ?? fetchResultBytes;
  const params = input.params ?? {};
  const latchKey = caseStageLatchKey(jobId, stage, attempt);

  // 1 — what the cloud resolved for THIS stage. Read first, and never cached from an earlier
  // stage: the prompt and the reference list both change between clear and magenta.
  onPhase?.("reading");
  const job = await client.caseJobs.get(jobId);
  if (job.executor !== "device") {
    throw new CaseStageError(
      "This job runs on the platform's account. The cloud worker generates it; this machine does not.",
      true,
    );
  }
  if (!stageOpen(job, stage)) {
    // Nothing to run and nothing to report: another run of this job already moved the stage.
    throw new CaseStageError(
      `This stage is not open on the job any more (it is ${job.stage}). Nothing was submitted.`,
      true,
    );
  }
  const attemptToken = job.deviceAttemptToken ?? undefined;

  // 2 — the latch. `submitting` is a DEAD END on purpose: this client cannot ask the vendor
  // "what did I start a minute ago", so the only honest answer is to stop and tell the
  // operator to look at their account. Nothing is posted to the job: a `stage-fail` here would
  // claim nothing was sent, and that is exactly what is not known.
  const latched = readLatch(latchKey);
  if (latched?.state === "submitting") throw new CaseStageError(UNKNOWN_ATTEMPT_MESSAGE, true);

  /* Declared OUTSIDE the try because it is read in step 6, after the catch that a failed quote
     falls through. A quote that failed leaves it `undefined`, and `stageResult` then sends no
     credits rather than a zero — a recorded 0 credits is a wrong number on the Usage page, and
     an absent one is an honest gap. */
  let estimated: number | undefined;
  let jobIds: string[];

  if (latched?.state === "submitted" && latched.jobIds?.length) {
    // The submission succeeded and only the WAIT was interrupted. Resuming is free; the
    // expensive mistake would be to generate the same image again.
    jobIds = latched.jobIds;
    logger?.log("info", `Higgsfield · resuming stage ${stage} of job ${jobId} on the generation already submitted (${jobIds.join(", ")}).`);
    onPhase?.("submitted");
  } else {
    const prompt = job.promptText;
    /* `inputs` is keyed in SEND ORDER by the server (product first, then references), and a
       null value is a reference the server could not sign — refused below, before a credit. */
    const inputUrls = Object.values(job.inputs ?? {});
    if (inputUrls.some((u) => !u)) {
      throw new CaseStageError(
        "The cloud could not sign every reference photo for this stage, so nothing was sent to Higgsfield.",
      );
    }
    if (!prompt) {
      // Refused BEFORE submitting, so nothing is spent. Inventing a local prompt here is how
      // the two paths start producing different pictures without anyone noticing.
      throw new CaseStageError(
        "The cloud did not send a resolved prompt for this stage, so there is nothing to send to Higgsfield.",
      );
    }
    if (inputUrls.length === 0) {
      throw new CaseStageError(
        "The cloud did not send links to the reference photos, so there is nothing to send to Higgsfield.",
      );
    }

    // 3 — quote the cost. `get_cost` submits nothing, so this is safe to do every time.
    onPhase?.("estimating");
    try {
      const { credits } = await mcp.hfMcpEstimate(model, params);
      onEstimate?.(credits);
      estimated = credits;
    } catch (e) {
      // A quote that fails is not a reason to refuse a run whose price is recorded — the
      // vendor will refuse it for real if the credits are not there.
      logger?.log("warn", `Higgsfield cost estimate failed — ${errText(e)}`);
      if (listCreditsFor(model, params) === undefined) throw new CaseStageError(noQuoteRefusal(model));
    }

    // 4 — submit, with the references in the order the cloud listed them. The latch is written
    // BEFORE the request leaves: if the process dies between these lines, the record is what
    // tells the next attempt "we do not know whether this was billed".
    const idem = newIdempotencyKey();
    markSubmitting(latchKey, idem);
    try {
      const submitted = await mcp.hfMcpGenerate({
        model, prompt, params: { ...params, client_request_id: idem }, inputUrls: inputUrls as string[],
      });
      jobIds = submitted.jobIds;
    } catch (e) {
      // The request left and nothing came back: the latch stays `submitting`, the stage stays
      // open, and the operator is told to look before anything is spent again. The reason is
      // LOGGED: once the operator dismisses the banner it is the only record of what happened.
      logger?.log("error", `Higgsfield · stage ${stage} of job ${jobId}: the submission's answer was lost — ${errText(e)}`);
      throw new CaseStageError(`${errText(e)} — ${UNKNOWN_ATTEMPT_MESSAGE}`, true);
    }
    markSubmitted(latchKey, idem, jobIds);
    onPhase?.("submitted");
  }

  try {
    // 5 — wait.
    onPhase?.("waiting");
    const status = await waitForTerminal(mcp, jobIds, input.waitBudgetMs);
    const reason = failureSentence(status.status, status.error);
    if (reason) throw new VendorAnsweredError(reason);
    if (!status.urls.length) {
      throw new CaseStageError("Higgsfield reported the image was finished but sent no link to it.");
    }

    // 6 — the bytes go to the cloud, and only now is this a success.
    onPhase?.("uploading");
    const { bytes, mediaType } = await fetchBytes(status.urls[0]);
    const next = await client.caseJobs.stageResult(jobId, bytes, {
      credits: estimated, model, mediaType, ...(attemptToken ? { attemptToken } : {}),
    });
    clearLatch(latchKey);
    onPhase?.("done");
    return next;
  } catch (e) {
    if (isStaleAttempt(e)) {
      // Superseded: another run of this job moved the stage first. Nothing to resubmit, nothing
      // to fail — the job as it stands NOW is the answer, so it is re-read and handed back.
      clearLatch(latchKey);
      logger?.log("warn", `Higgsfield · stage ${stage} of job ${jobId}: ${STALE_ATTEMPT_MESSAGE}`);
      return client.caseJobs.get(jobId);
    }
    if (e instanceof HttpError && e.status === 409) {
      // Any OTHER refusal of the report ("stage already finished", a request-id mismatch): the
      // stage may have been finished by another report. The job is re-read, and when this
      // attempt is no longer the open one, the latch and the photos→job mapping are cleared —
      // there is nothing left for either to protect. Nothing is posted: the job already says
      // what happened. While the stage IS still open at this attempt, both are kept: the
      // vendor job is real, and the next run resumes it.
      const now = await client.caseJobs.get(jobId);
      const openHere = stageOpen(now, stage) && (now.attempts?.[stage] ?? 0) === attempt;
      if (!openHere) {
        clearLatch(latchKey);
        if (input.runKey) forgetRun(input.runKey);
        throw new CaseStageError(
          `Another report finished this stage first (${now.stage}); nothing was submitted again, and the card shows the job as it stands.`,
          true,
        );
      }
      throw new CaseStageError(
        `The cloud refused this report (${(e.body as { detail?: string } | undefined)?.detail ?? e.message}); the stage is still open, so the next run resumes the same vendor job.`,
        true,
      );
    }
    const message = e instanceof Error ? e.message : String(e);
    const already = e instanceof CaseStageError && e.reported;
    if (!already) {
      // Best-effort: if the cloud is unreachable too, the ORIGINAL failure is still the one the
      // operator needs, so a failing report must not replace the reason for it. An unreported
      // stage also stays open forever, and no worker will ever pick it up.
      try {
        if (e instanceof VendorAnsweredError) {
          // Not charged: failed, not held — there is nothing to check.
          if (attemptToken) await client.caseJobs.stageFail(jobId, message, attemptToken);
          else await client.caseJobs.stageFail(jobId, message);
        } else {
          // The vendor has the body and issued an id. HELD: the image may exist and may be
          // billed; the id is what the owner checks, and what "Check again" asks the vendor for.
          const note = message.slice(0, 500);
          if (attemptToken) await client.caseJobs.stageHeld(jobId, jobIds[0]!, note, attemptToken);
          else await client.caseJobs.stageHeld(jobId, jobIds[0]!, note);
        }
        clearLatch(latchKey);
      } catch (reportError) {
        if (isStaleAttempt(reportError)) clearLatch(latchKey);
        /* the message below is still raised; the latch keeps the vendor ids for a resume */
      }
    }
    throw new CaseStageError(message, true);
  }
}

export interface ResumeHeldCaseJobStageInput {
  client: JsonClient;
  jobId: string;
  stage: CaseStageKey;
  mcp?: HiggsfieldMcpClient;
  fetchBytes?: typeof fetchResultBytes;
}

/**
 * "Check again" on a HELD stage, device path.
 *
 * Re-reads the job. When it is still held on a REAL vendor id (not a `local:` marker), asks
 * the vendor ONCE for that job — `jobs_wait` on the known id submits nothing — and, if it has
 * finished, posts the late image with `request_id`, which the server stores against the
 * request it holds without spending an attempt: that image is already paid for. Still running
 * → the job is returned as it is. A refusal (moderated / failed / canceled) is raised as the
 * sentence to show, and NOTHING is posted: the stage stays held with the vendor's answer, and
 * the operator's retry is a fresh, uncharged attempt. A cloud job is only re-read — the worker
 * does its own looking-up.
 */
export async function resumeHeldCaseJobStage(input: ResumeHeldCaseJobStageInput): Promise<CaseJobView> {
  const { client, jobId, stage } = input;
  const mcp = input.mcp ?? higgsfieldMcp();
  const fetchBytes = input.fetchBytes ?? fetchResultBytes;
  const job = await client.caseJobs.get(jobId);
  const requestId = job.requestIds?.[stage];
  if (job.executor !== "device" || job.stage !== `${stage}_held` || !requestId || requestId.startsWith("local:")) {
    return job;
  }
  if (!mcp.hfMcpAvailable) return job;
  const r = await mcp.hfMcpWait([requestId]);
  const vendor = r.jobs.find((j) => j.jobId === requestId) ?? r.jobs[0];
  if (!vendor) return job;
  // Still queued or running: nothing to post, nothing to say — the job stays as it is.
  if (!isTerminalState(vendor.status)) return job;
  const reason = failureSentence(vendor.status, vendor.error);
  if (reason) throw new CaseStageError(reason, true);
  if (!vendor.urls.length) return job;
  const { bytes, mediaType } = await fetchBytes(vendor.urls[0]);
  return client.caseJobs.stageResult(jobId, bytes, {
    requestId,
    model: job.models?.[stage],
    mediaType,
    ...(job.deviceAttemptToken ? { attemptToken: job.deviceAttemptToken } : {}),
  });
}

/** Poll `jobs_wait` until the job is terminal or the budget runs out. */
async function waitForTerminal(
  mcp: HiggsfieldMcpClient,
  jobIds: string[],
  budgetMs = DEVICE_WAIT_BUDGET_MS,
) {
  const deadline = Date.now() + budgetMs;
  for (;;) {
    const r = await mcp.hfMcpWait(jobIds);
    const job = r.jobs.find((j) => j.jobId === jobIds[0]) ?? r.jobs[0];
    if (!job) throw new CaseStageError("Higgsfield lost track of the generation job.");
    if (r.allTerminal || isTerminalState(job.status)) return job;
    if (Date.now() >= deadline) {
      throw new CaseStageError(
        "Higgsfield did not finish this image in time. It may still complete on higgsfield.ai.",
      );
    }
    const pauseMs = Math.max(0, (r.pollAfterSeconds ?? 0) * 1000);
    if (pauseMs > 0) await new Promise((res) => setTimeout(res, pauseMs));
  }
}

function isTerminalState(s: string): boolean {
  return s === "completed" || s === "failed" || s === "nsfw" || s === "canceled";
}

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
